import { useCallback } from "react";
import { errAsync, okAsync, ResultAsync } from "neverthrow";
import { useNavigate } from "react-router";

import type {
  ConflictError,
  InternalError,
  InvalidError,
  NotFoundError,
} from "@thalamos/common";
import { buildInternalError, buildInvalidError } from "@thalamos/common";
import type { UseSingleUseActionTokenResponse } from "@thalamos/aspire-common";

import type { UploadBulkImportCSVProps } from "../../types.js";
import { AssignPatientNhsNumberActionInner } from "../AssignPatientNhsNumberAction/AssignPatientNhsNumberAction.js";
import { api } from "~/api.js";
import { routeFns } from "~/routePaths.js";

export function PatientBulkImportAction({
  token,
}: {
  token: UploadBulkImportCSVProps["singleUseActionToken"];
}) {
  const navigate = useNavigate();
  const uploadCsvFn = useCallback(
    (
      tokenId: string,
      csvData: string,
      dryRun: boolean,
    ): ResultAsync<
      UseSingleUseActionTokenResponse,
      InvalidError | NotFoundError | ConflictError | InternalError
    > => {
      return ResultAsync.fromPromise(
        api.singleUseActionToken.use(tokenId, csvData, dryRun),
        (err) =>
          buildInternalError({
            message: err instanceof Error ? err.message : String(err),
          }),
      ).andThen((response) => {
        if (response.status === 200) {
          return okAsync({ type: "patientBulkImport" as const });
        } else if (response.status === 400) {
          const data = response.data as unknown as {
            message?: string;
            issues?: string[];
          };
          const message =
            data?.message ?? "Unknown error - Please contact Thalamos";
          // Preserve per-row `issues` so the banner can list each failing row.
          return errAsync(buildInvalidError({ message, issues: data?.issues }));
        } else {
          // Non-validation failures (5xx, transport) are server errors — map to
          // InternalError without `issues`.
          const data = response.data as unknown as { message?: string };
          const message =
            data?.message ?? "Unknown error - Please contact Thalamos";
          return errAsync(buildInternalError({ message }));
        }
      });
    },
    [],
  );

  return (
    <AssignPatientNhsNumberActionInner
      singleUseActionToken={token}
      uploadCsvFn={uploadCsvFn}
      onSuccess={() => {
        navigate(routeFns.home());
      }}
    />
  );
}
